import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { Box, Typography } from '@mui/material';
import useSubscriptions from '../../../store/models/subscriptions/useSubscriptions';
import useSubscriptionDialogs from './useSubscriptionDialogs.jsx';
import SubscriptionCardContainer from './subscriptionCardContainer';

const sectionLabel = {
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: 'var(--ecal-lightgrey)',
    mb: 1.5,
};

function SubscriptionsPanel() {
    const {
        subscriptions,
        inactiveSubscriptions,
        isLoading,
        unsubscribe,
        removeSchedule,
        addSchedule,
        loadAvailableSchedules,
    } = useSubscriptions();

    const {
        openUnsubscribeDialog,
        openRemoveDialog,
        openAddSuccessDialog,
        renderDialogs,
    } = useSubscriptionDialogs({ unsubscribe, addSchedule, removeSchedule });

    useEffect(() => {
        subscriptions.forEach((sub) => {
            loadAvailableSchedules(sub.id);
        });
    }, [subscriptions]);

    function handleAddSchedule(subscriptionId, scheduleId) {
        addSchedule(subscriptionId, scheduleId);
        openAddSuccessDialog();
    }

    function renderCard(subscription) {
        return (
            <SubscriptionCardContainer
                key={subscription.id}
                subscription={subscription}
                onUnsubscribe={() => openUnsubscribeDialog(subscription)}
                onRemoveSchedule={openRemoveDialog}
                onAddSchedule={handleAddSchedule}
            />
        );
    }

    function renderInactiveCard(subscription) {
        return (
            <SubscriptionCardContainer
                key={subscription.id}
                subscription={subscription}
            />
        );
    }

    function renderHeader() {
        return (
            <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--ecal-lightblue)', mb: 0.5 }}>
                    Manage My ECAL
                </Typography>
                <Typography variant="h2" sx={{ fontWeight: 600, color: 'var(--ecal-white)', fontSize: { xs: '1.5rem', md: '2rem' } }}>
                    My subscriptions
                    {subscriptions.length > 0 && (
                        <>
                            {' · '}
                            <Box component="span" sx={{ background: 'linear-gradient(90deg, #00D7FF, #0078FF)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>
                                {subscriptions.length}
                            </Box>
                        </>
                    )}
                </Typography>
                <Typography sx={{ color: 'var(--ecal-lightgrey)', mt: 0.5, fontSize: '0.875rem' }}>
                    Add or remove schedules per publisher. Changes sync to your calendar instantly.
                </Typography>
            </Box>
        );
    }

    function renderEmpty() {
        return (
            <Box sx={{
                background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.015) 100%)',
                border: '1px solid rgba(187,188,188,0.12)',
                borderRadius: '22px',
                py: 8,
                textAlign: 'center',
                color: 'var(--ecal-lightgrey)',
            }}>
                <Typography>You don't have any active subscriptions yet.</Typography>
                <Typography sx={{ fontSize: '0.875rem', mt: 0.5 }}>Browse the directory to add one.</Typography>
            </Box>
        );
    }

    function renderActiveList() {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {subscriptions.map(renderCard)}
            </Box>
        );
    }

    function renderInactiveList() {
        if (!inactiveSubscriptions.length) return null;
        return (
            <Box sx={{ mt: 4 }}>
                <Typography sx={sectionLabel}>Previously synced</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {inactiveSubscriptions.map(renderInactiveCard)}
                </Box>
            </Box>
        );
    }

    if (isLoading) {
        return (
            <Box sx={{ color: 'var(--ecal-lightgrey)', py: 4 }}>
                <Typography>Loading subscriptions…</Typography>
            </Box>
        );
    }

    return (
        <Box>
            {renderHeader()}
            {subscriptions.length === 0 ? renderEmpty() : renderActiveList()}
            {renderInactiveList()}
            {renderDialogs()}
        </Box>
    );
}

export default observer(SubscriptionsPanel);
